// Compact "Add to pipeline" menu used by the Leads bulk-action bar.
// Pops a small panel anchored to its trigger; user picks pipeline → stage,
// then we fire the existing useBulkAddLeads hook against the selected
// leadIds. We default mode='skip-existing' so adding a lead that's already
// in the picked pipeline is a no-op rather than a silent re-shuffle.

import React, { useState } from "react";
import toast from "react-hot-toast";
import { GitBranch, ChevronRight } from "lucide-react";
import { Button } from "../atoms";
import { usePipelines, useBulkAddLeads } from "../hooks/usePipelines";

export default function AddToPipelineMenu({ leadIds, onDone }) {
  const { data, isLoading } = usePipelines();
  const pipelines = data?.pipelines || [];
  const [open, setOpen] = useState(false);
  const [activePipelineId, setActivePipelineId] = useState(null);
  const bulkAdd = useBulkAddLeads();

  const activePipeline = pipelines.find((p) => String(p._id) === String(activePipelineId)) || pipelines[0] || null;

  const submit = (stageId) => {
    if (!activePipeline) return;
    bulkAdd.mutate(
      { pipelineId: activePipeline._id, leadIds, stageId, mode: "skip-existing" },
      {
        onSuccess: (res) => {
          const s = res?.summary || {};
          const parts = [];
          if (s.added) parts.push(`${s.added} added`);
          if (s.moved) parts.push(`${s.moved} moved`);
          if (s.skipped) parts.push(`${s.skipped} already there`);
          if (s.notFound) parts.push(`${s.notFound} not found`);
          toast.success(parts.join(" · ") || "Done");
          setOpen(false);
          onDone?.();
        },
        onError: (err) => toast.error(err?.response?.data?.error || "Add failed"),
      },
    );
  };

  return (
    <div style={{ position: "relative" }}>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<GitBranch size={14} />}
        onClick={() => setOpen((v) => !v)}
        disabled={pipelines.length === 0 || isLoading}
      >
        Add to pipeline
      </Button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 1200 }} />
          <div
            style={{
              position: "absolute", bottom: "calc(100% + 6px)", left: 0,
              background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)",
              borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
              minWidth: 380, zIndex: 1201, overflow: "hidden", display: "grid", gridTemplateColumns: "1fr 1fr",
            }}
          >
            <div style={{ borderRight: "1px solid var(--color-border-subtle)", maxHeight: 280, overflowY: "auto" }}>
              <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", }}>Pipeline</div>
              {pipelines.map((p) => (
                <button
                  key={p._id}
                  onClick={() => setActivePipelineId(p._id)}
                  style={{
                    width: "100%", textAlign: "left", padding: "8px 12px", border: "none", cursor: "pointer",
                    background: String(activePipeline?._id) === String(p._id) ? "var(--color-bg-muted)" : "transparent",
                    color: "var(--color-text-primary)", fontSize: 13,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                  <ChevronRight size={13} style={{ color: "var(--color-text-secondary)" }} />
                </button>
              ))}
            </div>
            <div style={{ maxHeight: 280, overflowY: "auto" }}>
              <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", }}>Stage</div>
              {(activePipeline?.stages || []).length === 0 && (
                <div style={{ padding: 12, fontSize: 12, color: "var(--color-text-secondary)" }}>No stages in this pipeline.</div>
              )}
              {(activePipeline?.stages || []).map((s) => (
                <button
                  key={s._id}
                  onClick={() => submit(s._id)}
                  disabled={bulkAdd.isPending}
                  style={{
                    width: "100%", textAlign: "left", padding: "8px 12px", border: "none", cursor: "pointer",
                    background: "transparent", color: "var(--color-text-primary)", fontSize: 13,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{s.name}</span>
                    <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{(s.leads || []).length}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
